/* eslint-disable @nx/enforce-module-boundaries */
import { Component, inject } from '@angular/core';
import {
  FormGroup,
  NonNullableFormBuilder,
  Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { DriverService } from '../../../api/services/driver.service';
import { NotificationService } from '@t-llevo/common-ui';
import { HttpErrorResponse } from '@angular/common/http';
import { AddDriverForm } from '../../models/AddDriverForm';
import { PersonalFormComponent } from './personal-form/personal-form.component';
import { CredentialsFormComponent } from './credentials-form/credentials-form.component';
import { VehicleFormComponent } from './vehicle-form/vehicle-form.component';
import { LicensesFormComponent } from './licenses-form/licenses-form.component';
import {
  passwordValidators,
  emailValidators,
} from 'admin-drivers/src/app/common/validators';
import { DialogStepperFormComponent } from 'admin-drivers/src/app/common/dialog/components/dialog-stepper-form/dialog-stepper-form.component';
import { User } from 'admin-drivers/src/app/common/models/User';

@Component({
  selector: 't-llevo-driver-add',
  standalone: true,
  imports: [DialogStepperFormComponent],
  template: `
    <t-llevo-dialog-stepper-form
      [frm]="frm"
      dialogTitle="Adicionar Conductor"
      [steps]="steps"
      (dataSubmit)="onSubmit($event)"
      [isLinear]="isLinear"
      [isStepperLoading]="loading"
    >
    </t-llevo-dialog-stepper-form>
  `,
  styles: `
    .step-actions {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-self: flex-end;
      padding: 0.6rem 1.2rem;
    }
  `,
})
export class DriverAddComponent {
  private notifSrv = inject(NotificationService);
  private driverSrv = inject(DriverService);

  isLinear = true;
  loading = false;

  private date = new Date().getFullYear();

  frm = this._fb.record({
    personal: this._fb.group({
      selectedUser: this._fb.control<User | null>(null),
      name: ['', Validators.required],
      lastname: ['', Validators.required],
      phone: ['', [Validators.required, Validators.pattern(/[0-9]{8}/)]],
      email: ['', emailValidators],
      birthday: ['', Validators.required],
      dni: [
        '',
        [Validators.required, Validators.pattern(/^\d{11}$|^[A-Za-z]\d{6}$/)],
      ],
      photo: this._fb.control<File | null>(null, [Validators.required]),
    }),
    licenses: this._fb.array([
      this._fb.group({
        categories: this._fb.control<string[]>([], [Validators.required]),
        licenseNumber: [
          '',
          [
            Validators.required,
            Validators.pattern(/^[0-9]{11}$|^[a-zA-Z0-9]{7}$/),
          ],
        ],
        expeditionDate: ['', Validators.required],
        expiredYear: [
          this.date.toString(),
          [
            Validators.required,
            Validators.min(new Date().getFullYear()),
            Validators.max(2050),
            Validators.pattern(/^[0-9]{4}$/),
          ],
        ],
        image: this._fb.control<File | null>(null, [Validators.required]),
      }),
    ]),
    vehicle: this._fb.group({
      vehicleType: ['', Validators.required],
      mark: ['', Validators.required],
      model: ['', Validators.required],
      color: ['', Validators.required],
      plate: [
        '',
        [Validators.required, Validators.pattern(/^[a-zA-Z]{1}[0-9]{5}$/)],
      ],
    }),
    credentials: this._fb.group({
      password: ['', passwordValidators],
      confirmPassword: ['', passwordValidators],
    }),
  });

  steps = [
    {
      label: 'Personal',
      component: PersonalFormComponent,
      controlName: 'personal',
    },
    {
      label: 'Licencias',
      component: LicensesFormComponent,
      controlName: 'licenses',
    },
    {
      label: 'Vehículo',
      component: VehicleFormComponent,
      controlName: 'vehicle',
    },
    {
      label: 'Credenciales',
      component: CredentialsFormComponent,
      controlName: 'credentials',
    },
  ];

  constructor(
    public dialogRef: MatDialogRef<DriverAddComponent>,
    private _fb: NonNullableFormBuilder,
  ) {
    this.selectedUser.valueChanges.subscribe((data: unknown) => {
      if (data && this.frm.controls['credentials']) {
        this.frm.removeControl('credentials');
        this.steps = this.steps.filter((step) => step.label !== 'Credenciales');
      } 
      else if (!data && !this.frm.controls['credentials']) {
        this.frm.addControl(
          'credentials',
          this._fb.group({
            password: ['', passwordValidators],
            confirmPassword: ['', passwordValidators],
          }),
        );

        if (!this.steps.find((step) => step.label === 'Credenciales')) {
          this.steps = [
            ...this.steps,
            {
              label: 'Credenciales',
              component: CredentialsFormComponent,
              controlName: 'credentials',
            }
          ]
        }
      }
      this.frm.updateValueAndValidity();
    });
  }

  onSubmit(data: AddDriverForm) {
    this.loading = true;
    this.driverSrv.createDriver(data).subscribe({
      next: () => {
        this.notifSrv.dispatch('Conductor creado', 'success');
        this.dialogRef.close(true);
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.notifSrv.dispatch(err.error.message, 'error');
      },
    });
  }

  get selectedUser() {
    return (this.frm.controls['personal'] as FormGroup).controls[
      'selectedUser'
    ];
  }
}
